/**
 * 前端權限判斷（WP-A3，B2 批）——**純函式層**，不依賴 React / Next / fetch。
 *
 * 背景（migration/docs/製作順序規劃.md §6「權限不前移是全案最貴的返工」）：
 * 後端的 fail-closed 路由守衛已於本批完成（api/app/core/route_permissions.py，
 * 23 支路由零漏登記），但 admin 前端在此之前**只有選單過濾**——純顯示層。
 * 任何已登入者把網址直接打進網址列就進得去任何頁面，頁面骨架照畫、請求照發，
 * 只是每一個請求各自回一個錯誤。本檔與 components/RequirePermission.tsx 一起
 * 把「進不進得去」從顯示層提到路由層。
 *
 * 語意對齊舊系統
 * --------------
 * 權限節點＝舊系統 `auth_item.name`，形如 `/admin/<模組>/<動作>`；判斷語意就是舊站
 * common/components/AccessControl.php 的 `$user->can($permission)`（不成立即
 * ForbiddenHttpException → 403）。字串一律取自後端註冊表
 * （api/app/core/route_permissions.py）與 nav/menu.ts 的 permission 欄位，
 * 兩者同源於 Firestore `rbacItems`。
 * ⛔ 業主裁決 2026-09-03：**照搬舊系統現況，不新增也不推測**——本檔不定義任何
 *    權限節點常數，只消費既有字串。
 *
 * ⛔ fail closed
 * --------------
 * 取不到權限資料（/me 尚未回來、失敗、閉包是空陣列、欄位型別不對）一律**視為沒有
 * 權限**。can() 沒有任何一條「資料缺失就放行」的路徑；「還在載入」與「沒有權限」
 * 的區別由 decideAccess() 的 pending 旗標表達，由呼叫端決定要不要先畫東西——
 * 但 pending 一樣不會畫出被保護的內容。
 */

import type { NavItem, NavSpec } from "@ccheng/ui";
import type { MeResponse } from "./apiClient";

/**
 * 萬用權限。後端 GET /api/v1/me 對 Super Administrator 回 `permissions: ["*"]`
 * 的既有契約（B0 上線，本批不動）。
 */
export const SUPER_ADMIN_WILDCARD = "*";

/**
 * 判斷所需的最小身分切片——刻意不吃整個 MeResponse，讓 can() 可被任何持有
 * 「權限閉包 + 超管旗標」的呼叫端使用（測試、未來的伺服端渲染）。
 */
export type PermissionSubject = Pick<MeResponse, "isSuperAdministrator" | "permissions">;

/** 路由守衛的三態。pending 與 denied 都不會畫出被保護的內容。 */
export type AccessDecision = "granted" | "denied" | "pending";

export interface AccessInput {
  /** GET /api/v1/me 的回應；尚未取得／取得失敗一律 undefined。 */
  subject: PermissionSubject | null | undefined;
  /** 權限資料是否還在路上（含元件尚未 mount、尚未發出請求）。 */
  pending: boolean;
  /** 本路由要求的節點，any-of。空陣列＝舊系統沒有對應節點，路由層不設閘門。 */
  nodes: readonly string[];
}

/**
 * Super Administrator 的唯一判定點。
 *
 * 舊系統給它的 auth_assignment 是 **0 個權限節點**，靠 DbManager::checkAccess()
 * 的程式碼旗標放行；實測 rbacRoles/Super Administrator 的 permissionClosure 為
 * 空陣列，**絕不能拿它做交集比對**（比對結果永遠是「什麼都不能做」）。
 * 後端把這個 bypass 集中在 api/app/core/rbac.py 的 has_permission() 一處；
 * 前端對應地集中在這裡，can() 以外的任何地方都不准再寫一次超管判斷。
 */
function isSuperAdministrator(subject: PermissionSubject): boolean {
  if (subject.isSuperAdministrator === true) return true;
  return Array.isArray(subject.permissions) && subject.permissions.includes(SUPER_ADMIN_WILDCARD);
}

/**
 * 「這個使用者有沒有這個權限節點？」——全前端唯一的權限判斷式。
 *
 * fail closed 的四條路徑，任何一條成立都回 false：
 * 1. subject 是 undefined/null（/me 還沒回來、或整個失敗）
 * 2. permissions 不是陣列（回應格式異常）
 * 3. node 是空字串（呼叫端沒給節點——寧可擋掉也不要放行）
 * 4. 節點不在閉包內（含閉包為空陣列的情形）
 */
export function can(subject: PermissionSubject | null | undefined, node: string): boolean {
  if (!subject) return false;
  if (!Array.isArray(subject.permissions)) return false;
  if (isSuperAdministrator(subject)) return true;
  if (!node) return false;
  return subject.permissions.includes(node);
}

/**
 * any-of：持任一節點即通過，比照後端 RouteRule.permissions 的語意
 * （舊站同一頁掛多個 auth_item 的情形，如 templates 的 email/line/sms/pdf）。
 *
 * 注意 `nodes` 為空時回 **false**——「一個都沒列」在這一層是資料缺漏而非免權限。
 * 「這條路由本來就沒有對應節點」是路由層的語意，寫在 decideAccess()，不寫在這裡。
 */
export function canAny(
  subject: PermissionSubject | null | undefined,
  nodes: readonly string[],
): boolean {
  if (!subject) return false;
  if (!Array.isArray(subject.permissions)) return false;
  if (isSuperAdministrator(subject)) return true;
  return nodes.some((node) => can(subject, node));
}

/**
 * 由目前路徑反查「這一頁要求哪些權限節點」。
 *
 * 為什麼不是每頁自己宣告：120 頁分散宣告＝120 個可以忘記標的地方（後端就是被這件事
 * 咬過，見 route_permissions.py 的開頭說明）。nav/menu.ts 已經是「頁面 ↔ 權限節點」
 * 的既有對照表（93 個葉節點全部標了 permission，來源 legacy left.php 的
 * $identity->can(...)），路由守衛直接消費同一張表，選單與路由永遠不會各說各話。
 *
 * 比對方式（與 AdminShell 的麵包屑同規則）：
 * - 取 href 的**路徑部分**（丟掉 query）：menu.ts 有 9 個項目靠 ?view=／?type= 區分，
 *   usePathname() 不含 query，直接字串等值會全部落空。
 * - **前綴**比對並取最長匹配：`/web-management/faq/new`、`/faq/[id]` 這些子路由
 *   沿用清單頁的節點（舊站同一 controller 的 create/update 另有節點，但「進不進得去
 *   這個模組」的閘門就是 index；再細的授權由後端逐個 endpoint 擋）。
 * - 同一個 base 有多個項目（?type=1..4 的小天使專區）→ 全部收集成 any-of。
 */
export function requiredNodesForPath(
  nav: NavSpec,
  pathname: string | null | undefined,
): string[] {
  if (!pathname) return [];
  let bestLength = -1;
  let nodes: string[] = [];

  const visit = (items: readonly NavItem[]): void => {
    for (const item of items) {
      if (item.href) {
        const base = item.href.split("?")[0];
        const matched = base === "/" ? pathname === "/" : pathname === base || pathname.startsWith(`${base}/`);
        if (matched && base.length >= bestLength) {
          if (base.length > bestLength) {
            bestLength = base.length;
            nodes = [];
          }
          if (item.permission) nodes.push(item.permission);
        }
      }
      if (item.children?.length) visit(item.children);
    }
  };

  for (const group of nav) visit(group.items);
  return Array.from(new Set(nodes));
}

/**
 * 路由守衛的判定。呼叫端（RequirePermission）只做三選一的渲染，不再自行判斷。
 *
 * - pending：權限資料還在路上 → 不畫內容也不畫 403（避免授權使用者看到 403 閃一下）。
 * - nodes 為空：這條路徑在 nav/menu.ts 找不到對應項目（如後台首頁 `/`），舊系統也
 *   沒有對應的 auth_item → 路由層不設閘門。**但整個 (dashboard) 子樹仍需登入**
 *   （AdminShell 無 token 即導回 /login），且資料一律由後端 fail-closed 守衛把關。
 * - 其餘：any-of 命中才放行，否則 403。
 */
export function decideAccess({ subject, pending, nodes }: AccessInput): AccessDecision {
  if (pending) return "pending";
  if (nodes.length === 0) return "granted";
  return canAny(subject, nodes) ? "granted" : "denied";
}
